import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Tag } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { DATE_LOCALES } from "@/i18n";
import { Loading } from "@/components/common";
import { useTranslation } from "react-i18next";

export default function BlogDetail() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const dateLoc = DATE_LOCALES[i18n.language?.slice(0, 2)] || "en-IN";

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/public/blogs/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true));
  }, [slug, i18n.language]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32" data-testid="blog-not-found">
        <h1 className="font-serif text-4xl font-semibold text-navy">{t("blog.notFound")}</h1>
        <p className="text-muted-foreground mt-3">{t("blog.notFoundBody")}</p>
        <Link to="/blog" className="inline-flex items-center gap-2 mt-8 text-sm font-semibold uppercase tracking-wider text-gold-700">
          <ArrowLeft className="h-4 w-4" /> {t("blog.back")}
        </Link>
      </div>
    );
  }
  if (!data) return <Loading label={t("blog.loadingArticle")} />;

  const { post, related } = data;

  return (
    <div data-testid="blog-detail-page">
      <section className="bg-navy text-ivory hero-grain relative py-20">
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold hover:text-ivory transition-colors mb-8">
            <ArrowLeft className="h-4 w-4" /> {t("blog.allInsights")}
          </Link>
          <p className="text-xs font-semibold uppercase tracking-widest text-gold">{post.category}</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight mt-4 leading-tight">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-5 mt-6 text-sm text-ivory/60">
            <span>{post.author}</span>
            <span>{fmtDate(post.published_at, dateLoc)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {t("common.minRead", { count: post.read_time })}</span>
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-6 -mt-2">
        <img src={post.cover_image} alt={post.title} data-testid="blog-cover-image"
          className="w-full h-72 sm:h-96 object-cover shadow-card -translate-y-10 border-8 border-ivory" />
      </div>

      <article className="max-w-3xl mx-auto px-6 pb-10">
        <p className="font-serif text-2xl text-navy leading-snug border-l-4 border-gold pl-6 mb-10">{post.excerpt}</p>
        <div className="prose-legal" data-testid="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />
        {(post.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-border" data-testid="blog-tags">
            {post.tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted text-xs font-semibold uppercase tracking-wider text-navy-700">
                <Tag className="h-3 w-3 text-gold-700" /> {t}
              </span>
            ))}
          </div>
        )}
      </article>

      {related.length > 0 && (
        <section className="bg-white border-t border-border py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="font-serif text-3xl font-semibold text-navy mb-10">{t("blog.related")}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`} data-testid={`related-blog-${r.slug}`}
                  className="group bg-ivory border border-border hover:border-gold hover:shadow-hovercard transition-all duration-300">
                  <img src={r.cover_image} alt={r.title} className="w-full h-44 object-cover" />
                  <div className="p-6">
                    <p className="text-xs font-semibold uppercase tracking-widest text-gold-700">{r.category}</p>
                    <h3 className="font-serif text-xl font-semibold text-navy mt-2 leading-snug group-hover:text-gold-700 transition-colors">{r.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-navy hero-grain relative py-16">
        <div className="max-w-3xl mx-auto px-6 relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <h2 className="font-serif text-3xl font-semibold text-ivory">{t("blog.similar")}</h2>
            <p className="text-ivory/60 text-sm mt-2">{t("blog.similarBody")}</p>
          </div>
          <Link to="/book-consultation" data-testid="blog-detail-cta"
            className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-gold-600 transition-colors font-semibold uppercase tracking-wider text-sm px-8 py-4 shrink-0">
            {t("blog.book")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
